// ============================================================================
// EMBUDO — tablero kanban de contactos (C4-D1 / C7-D2).
// Arrastrar es 1:1 con el dedo/puntero (Apple: la tarjeta se queda pegada al
// punto donde la agarraste, con umbral de 8 px para no mover por accidente).
// SIEMPRE hay un camino sin arrastrar: cada tarjeta trae un selector "Mover a",
// que es además el camino de teclado y el cómodo en móvil.
//
// T-EMB-1 (R-EMB-01 · R-EMB-02): el dueño administra sus etapas desde aquí
// (`EtapasEditor`), y las etapas FINALES —Vendido, Postventa, Desertado…— van
// PLEGADAS en una franja «Cerradas» al final del tablero, para que lo que se ve
// sea lo que está vivo. Decisión de Cristhian (2-sep-2026): plegadas, no
// siempre visibles. El plegado se recuerda por navegador y por negocio.
//
// T-TAR-2 (R-TAR-01 · R-TAR-02): el embudo lee `v_embudo` (contacto + no leídos
// de sus conversaciones abiertas) y se suscribe al MISMO canal del inbox: cuando
// un cliente escribe, su tarjeta se enciende y sube al frente de su columna sin
// recargar. Lo dijo Cristhian: «que en el embudo yo tenga la tarjeta lista,
// señalándome que el cliente me escribió».
// ⛔ UNA sola suscripción por pantalla: `channel(topic)` devuelve el canal que
//    ya existe, y un `.on()` añadido después de `subscribe` no recibe nada, sin
//    error (medido, realtime-js 2.111). Lo que necesite eventos dentro del
//    embudo los recibe de aquí, no abre otra.
// ============================================================================
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronsLeft, ChevronsRight, GripVertical, Lock, Megaphone, SlidersHorizontal, User, Users, WifiOff } from "lucide-react";
import { Avatar, Boton, Chip, Vacio } from "./piezas.tsx";
import { EtapasEditor } from "./EtapasEditor.tsx";
import { ChipTemperatura, textoTemperatura } from "./Temperatura.tsx";
import { PanelContacto } from "./PanelContacto.tsx";
import {
  explicarError, fijarMoverAutomatico, leerMoverAutomatico, listarEmbudo, listarEtapas, moverEtapa, soltarCandado,
  suscribirInbox,
} from "./datos.ts";
import { desdeHace } from "./FranjaAlertas.tsx";
import {
  fechaCorta, laTieneAlguien, nadieLaHaTomado, nombreMostrado, porqueEnHumano, previoDeMensaje,
  type AutorMensaje, type Contacto, type ContactoEmbudo, type ContextoPanel, type Conversacion,
  type Etapa, textoAnuncio,
} from "./tipos.ts";

const UMBRAL = 8;   // px antes de considerar que es un arrastre y no un toque
/** T-CAL-17 · H1: las píldoras del hilo tras las que la temperatura YA está guardada. */
const PILDORAS_QUE_RECARGAN = new Set(["transferido_humano", "cliente_caliente"]);
/** T-CAL-17 · H1: ventana en la que varias píldoras seguidas se suman a UNA recarga. */
export const RECARGA_MS = 300;

/** Llave del plegado de «Cerradas», por negocio. `"0"` = desplegado. Por defecto, plegado. */
export function llavePlegado(tenantId: string): string { return `vx-embudo-plegado:${tenantId}`; }
function leerPlegado(tenantId: string): boolean {
  try { return localStorage.getItem(llavePlegado(tenantId)) !== "0"; } catch { return true; }
}
function guardarPlegado(tenantId: string, plegado: boolean) {
  try { localStorage.setItem(llavePlegado(tenantId), plegado ? "1" : "0"); } catch { /* sin storage: no pasa nada */ }
}

interface Arrastre {
  contactoId: string;
  desdeEtapa: string | null;
  x: number; y: number;          // posición del puntero
  dx: number; dy: number;        // offset respecto de la esquina de la tarjeta
  ancho: number; alto: number;
  activo: boolean;               // ya pasó el umbral
}

// ── T-LAT-7 · R-TAR-05 · ¿QUÉ LE PASA A ESTA CONVERSACIÓN? ──────────────────
// 🔴 EL DAÑO QUE ESTO CIERRA, y tiene nombre: Martha Lucia, 2-sep-2026. El bot
// escaló su conversación, nadie llegó, y **la única forma de encontrarla era
// abrir chat por chat**: el embudo no sabía a quién habías dejado esperando.
// ⛔⛔ Y NO BASTA CON MIRAR `estado_conversacion`: `humano` significa las dos
//    cosas a la vez — «el bot pidió un asesor y nadie ha llegado» y «una asesora
//    ya la tomó». Lo que las separa es **`asignada_a`**, y por eso la `0058` lo
//    saca a `v_embudo`.
// 🔴🔴 Y NO ES `motivo_humano`, aunque lo parezca (H-1 del fiscal, 2-sep-2026;
//    la primera versión de este tramo lo hizo mal y su vara pasaba EN VERDE por
//    el motivo equivocado): «tomar el chat» pone `asignada_a` y NO toca
//    `motivo_humano`, que sólo pasa a `'tomada'` cuando la persona ESCRIBE. Con
//    el predicado viejo, Sandra pulsaba «Tomar el chat», se iba a consultar un
//    precio, y la tarjeta seguía roja y primera de la columna mientras la
//    cabecera del hilo, a 30 cm, decía «Asesor».
// ⭐ LA REGLA VIVE EN `tipos.ts` Y LA COMPARTEN LAS TRES PANTALLAS (inbox, hilo y
//    esta tarjeta). Tres copias de una regla son tres reglas.
/** ¿Está en manos de una persona y NADIE la ha tomado todavía? */
export function esperaAsesor(c: ContactoEmbudo): boolean {
  return nadieLaHaTomado(c.estado_conversacion, c.asignada_a);
}
/** ¿Ya la tomó alguien del equipo? */
export function laAtiendeAlguien(c: ContactoEmbudo): boolean {
  return laTieneAlguien(c.estado_conversacion, c.asignada_a);
}

// ── I-40 · EL FRENTE DE LA COLUMNA ES LO FRESCO ─────────────────────────────
// 🔴 EL DAÑO QUE ESTO CIERRA (medido en producción el 12-sep-2026, «Sin etapa»
//    de Living House, 79 tarjetas):
//      · 14 «esperando asesor» ocupaban el frente y **la más nueva llevaba 152 h
//        parada** (6,3 días); la más vieja, 255 h. Ninguna con mensajes sin leer.
//      · debajo, 24 «sin leer», y **NI UNA era de las últimas 24 h**: la más
//        vieja, 224 h (9,4 días).
//      · solo **5 tarjetas de toda la columna** eran de las últimas 24 h ⇒ para
//        ver a quien escribió hace una hora había que pasar ~38 tarjetas muertas.
//    Cristhian, textual: «hay mensajes que ya están viejos y que ya no hay nada
//    que hacer. En cambio, los nuevos, siempre hay oportunidad de responder
//    rápido». Decisión suya del 12-sep: los escalones valen **solo dentro de 24 h**.
//
// ⛔⛔ ESTO **ENMIENDA** R-TAR-01 y R-TAR-05, que eran decisión suya del 3-sep.
//    No se borran: se acotan. Lo que se cae es el caso viejo, no el escalón.
//
// ⭐ POR QUÉ SE PIERDE TAN POCO, Y ES EL ARGUMENTO QUE SOSTIENE EL CAMBIO: una
//    tarjeta que pide asesor HACE UN RATO ya salía arriba por su actividad. El
//    escalón sólo cambiaba el orden de las que NO son recientes ⇒ en la práctica
//    servía únicamente para subir las muertas. Con el corte, lo urgente y fresco
//    sigue primero y lo urgente y podrido deja de tapar el frente.
//
// ⚠️ `ahora` se inyecta para que las varas no dependan del reloj de la máquina.
//    (Lección del 9-sep con V-67: una vara atada al reloj se cae sola.)
export const FRESCA_MS = 24 * 60 * 60 * 1000;

/** ¿Se movió en las últimas 24 h? Sin actividad NO es fresca: no se inventa. */
export function estaFresca(c: ContactoEmbudo, ahora: number = Date.now()): boolean {
  if (!c.ultima_actividad) return false;
  return ahora - new Date(c.ultima_actividad).getTime() < FRESCA_MS;
}

/** Dentro de una columna: primero A QUIEN DEJASTE ESPERANDO **si es de las
 *  últimas 24 h** (R-TAR-05, acotado por I-40), después las que esperan
 *  respuesta **también frescas**, después TODO por última actividad; sin
 *  actividad, al final.
 *  ⛔ Los dos escalones llevan el MISMO corte a propósito: acotar solo el
 *     primero dejaba 24 «sin leer» de hasta 9 días tapando el frente — medido,
 *     no supuesto. */
export function ordenarTarjetas<T extends ContactoEmbudo>(items: T[], ahora: number = Date.now()): T[] {
  const ts = (c: ContactoEmbudo) => (c.ultima_actividad ? new Date(c.ultima_actividad).getTime() : -1);
  return [...items].sort((a, b) => {
    const fa = estaFresca(a, ahora), fb = estaFresca(b, ahora);
    const ea = fa && esperaAsesor(a) ? 1 : 0, eb = fb && esperaAsesor(b) ? 1 : 0;
    if (ea !== eb) return eb - ea;
    const ua = fa && a.no_leidos > 0 ? 1 : 0, ub = fb && b.no_leidos > 0 ? 1 : 0;
    if (ua !== ub) return ub - ua;
    return ts(b) - ts(a);
  });
}

/** Cuántas de estas tarjetas NO estaban cuando se abrió la pantalla (R-TAR-01,
 *  I-40). Es lo que contesta «¿cuántos mensajes nuevos están entrando ahora?».
 *  ⛔ FALLA CERRADA: con la siembra sin hacer (`null`) devuelve **0**, jamás el
 *     total. Sin esto, al montar con 79 tarjetas el contador gritaría «+79» —
 *     un contador que miente en el arranque no se vuelve a mirar. */
export function cuantasNuevas<T extends { id: string }>(
  items: T[], alMontar: ReadonlySet<string> | null,
): number {
  if (!alMontar) return 0;
  let n = 0;
  for (const c of items) if (!alMontar.has(c.id)) n++;
  return n;
}

// ── T-ORD-8 · I-12 · EL TABLERO NO SE MUEVE BAJO LA MANO ────────────────────
// 🔴 EL DAÑO QUE ESTO CIERRA (medido el 4-sep-2026 en producción): «Sin etapa»
// de Living House tiene 45 tarjetas, 16 con sin leer y 10 esperando asesor.
// `ordenarTarjetas` se recalculaba EN CADA RENDER, así que abrir una tarjeta la
// hacía cruzar los tres escalones a la vez (marcar leída ⇒ pierde el escalón de
// «sin leer»; tomar el chat ⇒ pierde el de «espera asesor»; contestar ⇒ salta al
// frente por actividad): la que él estaba atendiendo se movía del sitio ~3 al
// ~11 y TODAS las de abajo se corrían una posición. Y el segundo camino del
// mismo dolor: un mensaje de un cliente cualquiera reordenaba la columna entera
// con el ratón encima. Cristhian, textual: «que no se reorganicen… para que no
// me haga perder».
// ⛔ `ordenarTarjetas` NO SE TOCA: sigue siendo la regla del ORDEN ÚTIL, y es la
//    que usan el montaje y las tarjetas que nunca se han visto (la miden V-182 y
//    V-231, que siguen valiendo tal cual).
// ⛔ NO HAY BOTÓN «Ordenar»: se propuso y Cristhian lo descartó el 4-sep con el
//    hueco advertido antes de decidir (una tarjeta que se encienda fuera de la
//    vista no se anuncia hasta que él recargue). El único camino de vuelta al
//    orden útil es RECARGAR / remontar la pantalla. No se compensa por la puerta
//    de atrás con contadores ni avisos flotantes.
// ⛔ Y el fijado NO se guarda en `localStorage`: recargar tiene que devolver el
//    orden útil, o una columna mal ordenada se volvería permanente sin salida.
/** Lo ya visto conserva su sitio; lo que nunca se pintó entra al frente por la
 *  regla de siempre. Con `fijado` vacío devuelve EXACTAMENTE `ordenarTarjetas`
 *  (el caso de arranque es el de siempre). */
export function ordenarConMemoria<T extends ContactoEmbudo>(items: T[], fijado: string[]): T[] {
  const sitio = new Map<string, number>();
  for (let i = 0; i < fijado.length; i++) if (!sitio.has(fijado[i])) sitio.set(fijado[i], i);
  const nuevas: T[] = [];
  const vistas: T[] = [];
  for (const c of items) (sitio.has(c.id) ? vistas : nuevas).push(c);
  vistas.sort((a, b) => sitio.get(a.id)! - sitio.get(b.id)!);
  return [...ordenarTarjetas(nuevas), ...vistas];
}

// ── T-CAL-17 · I-122 · I-123 · EL CARRIL «🔥 ATENDER YA» ────────────────────
// Decisión de Cristhian (26-sep): una columna «🔥 Atender ya». La FORMA sale de
// INVESTIGACION-EMBUDO-CRM §(a) Decisión 1: si la tarjeta SALIERA de su etapa se
// perdería dónde va la venta ⇒ el carril REFLEJA, no mueve (I-123): la tarjeta
// sigue en su columna y en el carril va una copia que solo ABRE el chat.
// ⛔⛔ LA REGLA ES LA DE LA TARJETA, NO UNA NUEVA: `esperaAsesor` (humano y nadie
//    la tomó, `asignada_a`) — NUNCA `motivo_humano` (H-1 de arriba). Y caliente
//    es la temperatura VIGENTE de `v_embudo` (0070), tal cual: aquí no se enfría.
// ⚠️ ORDEN: el que más lleva esperando primero. `v_embudo` no trae «desde cuándo
//    espera» (ninguna marca de la escalada), así que se usa `ultima_actividad`
//    ascendente — supuesto declarado en el acta de T-CAL-17: en `esperando` el
//    bot sigue hablando y eso también mueve `ultima_actividad`. Sin actividad, al
//    final: no se le inventa una espera.
/** Los calientes que esperan asesor, del que más lleva esperando al que menos. */
export function atenderYa<T extends ContactoEmbudo>(items: T[]): T[] {
  const ts = (c: ContactoEmbudo) =>
    (c.ultima_actividad ? new Date(c.ultima_actividad).getTime() : Number.POSITIVE_INFINITY);
  return items
    .filter((c) => c.temperatura === "caliente" && esperaAsesor(c))
    .sort((a, b) => ts(a) - ts(b));
}

/** La COPIA del carril. ⛔ Sin asa y sin «Mover a»: desde aquí no se cambia de
 *  etapa (I-123). ⛔ Y sin `data-contacto`: ese atributo es el de la tarjeta de
 *  la columna —lo busca `empezar` para arrastrar y lo leen las varas del orden—;
 *  la copia lleva `data-atender`. Abre por el MISMO `onAbrir` que la tarjeta. */
function TarjetaAtender({ c, onAbrir }: {
  c: ContactoEmbudo;
  onAbrir: (c: ContactoEmbudo, el: HTMLElement) => void;
}) {
  const nombre = nombreMostrado(c);
  const previo = c.ultimo_texto ?? "";
  const cuando = c.ultima_actividad ? desdeHace(c.ultima_actividad) : "";
  return (
    <article
      data-atender={c.id}
      role="button"
      tabIndex={0}
      aria-label={`Atender a ${nombre} · Esperando asesor${cuando ? ` · última actividad ${cuando}` : ""}`
        + (previo ? ` · ${previo}` : "") + textoTemperatura(c)}
      onClick={(e) => onAbrir(c, e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onAbrir(c, e.currentTarget);
      }}
      // ⛔ `relative` NO es adorno (medido a 375 px el 26-sep): el «caliente» del
      //    chip es `sr-only` (position:absolute) y, sin ancestro posicionado, se
      //    escapaba del scroll propio del carril y estiraba la PÁGINA a 422 px.
      className="relative w-[230px] sm:w-auto shrink-0 bg-superficie border border-peligro/30 rounded-xl p-2.5
        shadow-[var(--shadow-tarjeta)] cursor-pointer hover:border-peligro/60 transition-colors"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Avatar nombre={nombre} size={28} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-[13px] font-semibold truncate">{nombre}</p>
            <ChipTemperatura d={c} />
          </div>
          <p className="text-[11.5px] text-tinta-2 cifra truncate">
            Esperando asesor{cuando ? ` · ${cuando}` : ""}
          </p>
        </div>
      </div>
      {previo && <p className="mt-1.5 text-[12px] text-tinta truncate" title={previo}>{previo}</p>}
    </article>
  );
}

/** El carril. Se pinta UNA vez (trampa del panel: nada de copias `sm:hidden` /
 *  `hidden sm:block`, que un lector de pantalla leería dos veces): en celular es
 *  una franja arriba con scroll horizontal PROPIO; desde `sm`, una columna fija a
 *  la izquierda que no se desplaza con el tablero.
 *  ⚠️ VACÍO ⇒ plegado con «Nadie esperando», no desaparece: si se esfumara al
 *     tomar el último chat, todo el tablero saltaría bajo la mano (I-12).
 *  ⛔ NO lleva `data-etapa`: soltar un arrastre encima resuelve «ninguna columna»
 *     y no mueve nada (I-123). */
function CarrilAtenderYa({ items, onAbrir }: {
  items: ContactoEmbudo[];
  onAbrir: (c: ContactoEmbudo, el: HTMLElement) => void;
}) {
  const n = items.length;
  const vacio = n === 0;
  return (
    <section
      data-carril="atender-ya"
      role="region"
      aria-label={n === 1
        ? "Atender ya: 1 cliente caliente esperando asesor"
        : `Atender ya: ${n} clientes calientes esperando asesor`}
      className={`shrink-0 min-w-0 px-4 pb-3 sm:pb-5 sm:pl-6 sm:pr-0 flex flex-col
        ${vacio ? "sm:w-[calc(3.5rem+1.5rem)]" : "sm:w-[calc(260px+1.5rem)]"}`}
    >
      <div className={`min-w-0 sm:h-full flex flex-col rounded-xl border border-peligro/25 bg-peligro/5`}>
        <header className={`shrink-0 flex items-center gap-2 px-3 py-2
          ${vacio ? "sm:flex-col sm:py-3" : "border-b border-peligro/15"}`}>
          <h2 className={`text-[13.5px] font-semibold truncate ${vacio ? "sm:[writing-mode:vertical-rl] sm:rotate-180" : ""}`}>
            <span aria-hidden="true">🔥 </span>Atender ya
          </h2>
          {vacio
            ? <span className="ml-auto sm:ml-0 text-[12px] text-tinta-2 sm:[writing-mode:vertical-rl] sm:rotate-180">Nadie esperando</span>
            : <span data-conteo={n} className="ml-auto text-[12px] font-semibold text-peligro cifra">{n}</span>}
        </header>
        {!vacio && (
          <div className="flex gap-2 p-2 overflow-x-auto sm:overflow-x-visible sm:flex-col sm:flex-1 sm:overflow-y-auto scroll-fino">
            {items.map((c) => <TarjetaAtender key={c.id} c={c} onAbrir={onAbrir} />)}
          </div>
        )}
      </div>
    </section>
  );
}

// ── T-PRE-9 · R-PRE-01 · I-13 · LA MARCA DE AUTOR ───────────────────────────
// «Tú:» (una persona del equipo) · «Bot:» (el motor) · **nada** cuando habló el
// CLIENTE — lo del cliente es lo que se busca, y no se le pone etiqueta a lo que
// se busca. Un mensaje de `sistema` tampoco lleva marca: su propio previo ya
// dice «· evento del sistema», y anteponerle nada sería repetirlo.
// ⛔ NO se decide por `direccion` (`in`/`out`) aunque parezca lo mismo: el bot y
//    la asesora escriben los dos `out`, y la tarjeta tiene que separarlos — es el
//    dato con el que Cristhian sabe si ya contestó ALGUIEN o sigue el robot.
/** T-CAL-19 · I-130 · el texto del candado: el mismo en `aria-label`, `title` y lo leído. */
const CANDADO = "La movió una persona: el bot no la mueve";

// ── T-CAL-19 · I-128 · I-129 · EL INTERRUPTOR «EL BOT MUEVE LAS TARJETAS» ─────
// Solo se pinta si el negocio califica (`leerMoverAutomatico` ≠ null). El dueño lo
// cambia; cualquier otro rol lo ve DESHABILITADO con el porqué (title + texto
// accesible). ⛔ La base es la que manda (I-129: 42501 a todo el que no sea dueño):
// aquí solo se evita ofrecer un botón que va a fallar.
function InterruptorMover({ valor, puede, ocupado, onCambiar }: {
  valor: boolean; puede: boolean; ocupado: boolean; onCambiar: (v: boolean) => void;
}) {
  const porque = puede
    ? (valor ? "Encendido: el bot avanza las tarjetas solo hacia adelante y nunca toca las que movió una persona."
             : "Apagado: el bot no mueve ninguna tarjeta; se mueven a mano.")
    : "Solo el dueño del negocio puede cambiar esto.";
  return (
    <div className="relative mt-2 flex items-center gap-2 min-w-0" title={porque}>
      <button type="button" role="switch" aria-checked={valor} data-interruptor="mover"
        aria-label="El bot mueve las tarjetas"
        aria-describedby="vx-interruptor-mover-porque"
        disabled={!puede || ocupado}
        onClick={() => onCambiar(!valor)}
        className={`relative shrink-0 h-5 w-9 rounded-full border transition-colors
          ${valor ? "bg-marca border-marca" : "bg-elevado border-borde"}
          ${!puede || ocupado ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span aria-hidden="true" className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-[left]
          ${valor ? "left-[18px]" : "left-0.5"}`} />
      </button>
      <span className="text-[12.5px] text-tinta-2 truncate">
        El bot mueve las tarjetas{valor ? "" : " · apagado"}
      </span>
      <span id="vx-interruptor-mover-porque" className="sr-only">{porque}</span>
    </div>
  );
}

function marcaDeAutor(autor: AutorMensaje | null | undefined): string {
  if (autor === "humano") return "Tú:";
  if (autor === "bot") return "Bot:";
  return "";
}

function Tarjeta({
  c, etapas, arrastrando, onEmpezar, onMover, onAbrir, candado = false, onSoltar,
}: {
  c: ContactoEmbudo; etapas: Etapa[]; arrastrando: boolean;
  onEmpezar: (e: React.PointerEvent, c: Contacto) => void;
  onMover: (c: Contacto, etapaId: string | null) => void;
  /** T-LAT-3: abre el chat al costado, naciendo desde ESTA tarjeta. */
  onAbrir: (c: ContactoEmbudo, el: HTMLElement) => void;
  /** T-CAL-19 · I-130 · la movió una persona Y el negocio califica: se pinta el candado. */
  candado?: boolean;
  /** T-CAL-19 · «Soltar»: la devuelve al automático sin moverla. */
  onSoltar?: (c: ContactoEmbudo) => void;
}) {
  const nombre = nombreMostrado(c);
  const sinLeer = c.no_leidos > 0;
  // T-LAT-7 · R-TAR-05. El rótulo va TAMBIÉN al `aria-label`: hoy un lector de
  // pantalla no se entera de nada de esto, y quien lo use es quien más necesita
  // saber a quién dejó esperando sin tener que abrir chat por chat.
  const espera = esperaAsesor(c);
  const atendida = laAtiendeAlguien(c);
  const porque = espera ? " · Esperando asesor" : atendida ? " · La atiende una persona" : "";
  // ⚠️ `motivo_humano` no decide NADA aquí (eso lo hace `asignada_a`), pero sí
  //    dice el PORQUÉ, y eso vale para el `title`: «se aparcó porque falló
  //    nuestro motor» no es lo mismo que «lo pidió el cliente». Si no se sabe
  //    (fila anterior a la 0052), no se afirma nada.
  const elPorque = porqueEnHumano(c.motivo_humano);
  // ── T-PRE-9 · R-PRE-01 · I-13 · EL PREVIO ─────────────────────────────────
  // ⚠️ Ya viene NOMBRADO de `v_embudo` (`0059`, el mismo `case` que v_inbox): aquí
  //    no se decide nada sobre el tipo del adjunto. Lo único que se decide es si
  //    hay algo que decir.
  // ⛔ Sin previo NO SE PINTA LA LÍNEA — ni un guion, ni «…». Un contacto sin chat
  //    abierto no tiene nada que contar, y una raya ocupa el mismo sitio que un
  //    dato sin serlo.
  const previo = c.ultimo_texto ?? "";
  const marca = marcaDeAutor(c.ultimo_autor);
  const previoEntero = marca ? `${marca} ${previo}` : previo;
  // ── T-CAL-4 · R-TEM-01 · I-111 · LA TEMPERATURA ───────────────────────────
  // Sale tal cual de `v_embudo` (0070): aquí no se enfría ni se decide nada. Va
  // AL FINAL del `aria-label`, detrás del previo, sin tocar lo de delante (V-180
  // lee esa forma). Sin temperatura, cadena vacía: ni chip ni texto.
  // ⛔ NO entra en ningún orden: la tarjeta no se mueve por estar caliente (I-12).
  const temperatura = textoTemperatura(c);
  // ── T-CAL-19 · I-130 · EL CANDADO ─────────────────────────────────────────
  // Lo decide el embudo (`candado`: `etapa_puesta_por = 'equipo'` y la calificación
  // encendida); aquí solo se pinta. Va AL FINAL del `aria-label`, detrás de la
  // temperatura, sin tocar nada de delante (V-180 y V-479 leen esas formas).
  const textoCandado = candado ? ` · ${CANDADO}` : "";
  // ── T-ANU-10 · I-843 · EL ANUNCIO QUE LO TRAJO ─────────────────────────────
  // Sale tal cual de `v_embudo` (0097). AL FINAL del `aria-label`, detrás del
  // candado, sin tocar nada de delante (V-180 y V-479). Sin dato: nada, ni guion.
  const anuncio = textoAnuncio(c);
  return (
    <article
      data-contacto={c.id}
      role="button"
      tabIndex={0}
      // ⚠️ El «N sin leer» del final NO es adorno: es lo que lee la vara V-180 y
      // lo único que le dice a un lector de pantalla que esa tarjeta pide
      // respuesta. Lo de delante lo añadió T-LAT-3 (la tarjeta ahora ABRE).
      // ⭐ T-PRE-9: el previo va AL FINAL del `aria-label`, detrás de lo que ya
      // decía. Quien usa lector de pantalla es hoy el único que sigue sin poder
      // barrer el tablero de un vistazo: si el previo no entra aquí, la mejora
      // de este tramo simplemente no existe para él.
      aria-label={(sinLeer
        ? `Abrir la conversación de ${nombre} · ${c.no_leidos} sin leer${porque}`
        : `Abrir la conversación de ${nombre}${porque}`)
        + (previo ? ` · ${previoEntero}` : "")
        + temperatura + textoCandado + (anuncio ? ` · ${anuncio}` : "")}
      onClick={(e) => onAbrir(c, e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onAbrir(c, e.currentTarget);
      }}
      className={`relative bg-superficie border rounded-xl p-2.5 shadow-[var(--shadow-tarjeta)] transition-colors
        cursor-pointer
        ${arrastrando ? "opacity-30" : "hover:border-borde"}
        ${sinLeer ? "border-marca/40" : "border-borde-suave"}`}
    >
      {/* El punto: se ve de un vistazo, desde lejos, cuál tarjeta pide respuesta. */}
      {sinLeer && <span aria-hidden="true" className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-marca ring-2 ring-superficie" />}
      {c.titulo && (
        <p className="text-[13px] font-semibold tracking-tight truncate mb-1.5" title={c.titulo}>{c.titulo}</p>
      )}
      <div className="flex items-start gap-2">
        <button
          onPointerDown={(e) => onEmpezar(e, c)}
          // El asa arrastra; NO abre. (La otra mitad de la guarda es temporal y
          // vive en `abrir`: si la captura del puntero falla, el `click` de
          // después llega a la tarjeta y hay que pararlo igual.)
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={`Arrastrar ${nombre}`}
          title="Arrastrar a otra etapa"
          className="shrink-0 -ml-1 mt-0.5 p-1 rounded text-tinta-2/60 hover:text-tinta-2 cursor-grab
            active:cursor-grabbing touch-none"
        >
          <GripVertical size={15} />
        </button>
        <Avatar nombre={nombre} size={32} />
        <div className="min-w-0 flex-1">
          {/* T-CAL-4 · el chip va JUNTO AL NOMBRE; el nombre se recorta, el chip no. */}
          <div className="flex items-center gap-1.5 min-w-0">
            <p className={`text-[13.5px] truncate ${sinLeer ? "font-semibold" : "font-medium"}`}>{nombre}</p>
            <ChipTemperatura d={c} />
          </div>
          <p className="text-[11.5px] text-tinta-2 cifra truncate">{c.telefono_e164 ?? c.wa_id}</p>
        </div>
        {sinLeer && (
          <span className="shrink-0 min-w-[19px] h-[19px] px-1 grid place-items-center rounded-full
            bg-marca text-white text-[11px] font-semibold cifra" title={`${c.no_leidos} sin leer`}>{c.no_leidos}</span>
        )}
      </div>

      {/* T-PRE-9 · R-PRE-01 · lo último que se dijo, y quién lo dijo.
          ⚠️ `truncate` NO es adorno: sin él un mensaje largo parte la tarjeta en
          cinco renglones y la columna deja de barrerse de un vistazo — que es
          justo lo que este tramo viene a arreglar. El texto entero va al `title`.
          ⚠️ El tono sigue el criterio DEL INBOX (`Inbox.tsx:58`), no uno nuevo:
          sin leer ⇒ tinta fuerte, leído ⇒ apagado. */}
      {previo && (
        <p data-previo className={`mt-1.5 text-[12px] truncate ${sinLeer ? "text-tinta" : "text-tinta-2"}`}
          title={previoEntero}>
          {marca && <span className="text-tinta-2 font-medium">{marca} </span>}{previo}
        </p>
      )}

      {/* T-LAT-7 · R-TAR-05 · el distintivo. Se reúsa el MISMO chip que ya lleva
          el inbox (`Inbox.tsx:69`) y la cabecera del hilo (`Hilo.tsx:702`): el
          tono `peligro` de `piezas.tsx` nació justo para «Esperando asesor», es
          el único chip SÓLIDO de la pantalla y por eso se ve de un vistazo. ⛔ No
          se inventa un color nuevo: dos rótulos distintos para lo mismo en tres
          pantallas es cómo se pierde un lenguaje visual. */}
      {(espera || atendida) && (
        <div className="flex flex-wrap gap-1 mt-2">
          {espera
            ? <Chip tono="peligro" titulo={elPorque ?? "Nadie la ha tomado todavía"}>
                <User size={10} /> Esperando asesor
              </Chip>
            : <Chip tono="neutro" titulo={elPorque ?? "Alguien del equipo la tiene"}>
                <User size={10} /> La atiende una persona
              </Chip>}
        </div>
      )}

      {/* T-ANU-10 · I-843 · el anuncio que lo trajo. Una línea discreta, recortada. */}
      {anuncio && (
        <p data-anuncio className="mt-1.5 flex items-center gap-1 text-[11.5px] text-tinta-2 min-w-0" title={anuncio}>
          <Megaphone size={11} aria-hidden="true" className="shrink-0" />
          <span className="truncate">{anuncio}</span>
        </p>
      )}

      {c.etiquetas?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {c.etiquetas.slice(0, 3).map((t) => <Chip key={t} tono="neutro">{t}</Chip>)}
        </div>
      )}

      {/* T-CAL-19 · I-130 · el candado y su salida. Renglón PROPIO: en la fila de
          abajo no cabe junto al «Mover a» en 260 px. «Soltar» no abre el chat (igual
          que «Mover a») y no mueve la tarjeta: solo la devuelve al automático.
          ⚠️ `relative`: el `sr-only` es `position:absolute` y sin ancestro posicionado
          se escapa del scroll de la columna (trampa medida en T-CAL-17). */}
      {candado && (
        <div data-candado className="relative flex items-center gap-1.5 mt-2 min-w-0 text-[11.5px] text-tinta-2"
          title={CANDADO}>
          <Lock size={11} aria-hidden="true" className="shrink-0" />
          <span className="truncate">La movió una persona<span className="sr-only">: el bot no la mueve</span></span>
          {onSoltar && (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onSoltar(c); }}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Soltar ${nombre}: el bot vuelve a moverla`}
              title="El bot vuelve a moverla (solo hacia adelante). No cambia la etapa."
              className="ml-auto shrink-0 h-6 px-2 rounded-md border border-borde bg-elevado text-[11.5px] text-tinta
                hover:border-tinta/30 cursor-pointer"
            >
              Soltar
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        <span className="text-[11px] text-tinta-2 cifra" title={c.ultima_actividad ? "Última actividad" : "Creado"}>
          {c.ultima_actividad ? desdeHace(c.ultima_actividad) : fechaCorta(c.creado_en)}
        </span>
        <select
          value={c.etapa_id ?? ""}
          onChange={(e) => onMover(c, e.target.value || null)}
          // «Mover a» mueve; no abre el chat al costado.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={`Mover ${nombre} de etapa`}
          className="ml-auto h-8 max-w-[130px] px-2 rounded-lg bg-elevado border border-borde text-[12px]
            cursor-pointer focus:outline-none focus:border-marca/50"
        >
          <option value="">Sin etapa</option>
          {etapas.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
      </div>
    </article>
  );
}

const TarjetaMemo = memo(Tarjeta);

export function Embudo({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol } = ctx;
  const puedeEditarEtapas = rol === "dueno" || rol === "victorix";
  // T-CAL-19 · I-129 · solo el DUEÑO (la base usa `es_dueno`: el staff Victorix tampoco).
  const puedeCambiarMover = rol === "dueno";
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [contactos, setContactos] = useState<ContactoEmbudo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [arr, setArr] = useState<Arrastre | null>(null);
  const [destino, setDestino] = useState<string | null | undefined>(undefined);
  const [editorAbierto, setEditorAbierto] = useState(false);
  const [plegado, setPlegado] = useState<boolean>(() => leerPlegado(tenantId));
  const [canalCaido, setCanalCaido] = useState(false);
  /** T-ORD-8 · I-12 · el sitio de cada tarjeta, para TODO el tablero (no por
   *  columna: un contacto cambia de columna y su sitio lo sigue). Se siembra al
   *  montar y NO se refresca por nada que pase en vivo — ni por `recargar()`
   *  tras una caída del canal, que es el mismo daño con otro disfraz. */
  const [fijado, setFijado] = useState<string[]>([]);
  const fijadoRef = useRef<string[]>([]);
  fijadoRef.current = fijado;
  /** T-ORD-8 · I-12 · en qué columna quedó fijada cada tarjeta. Es lo que
   *  convierte «se movió de columna» en algo OBSERVABLE desde un solo sitio,
   *  venga el movimiento de donde venga. */
  const columnaFijada = useRef(new Map<string, string | null>());
  // I-40 · QUÉ HABÍA AL ABRIR LA PANTALLA. Nace en `null` —no en un Set vacío— y
  // ésa es la diferencia: `cuantasNuevas` devuelve 0 mientras valga `null`, así
  // que durante el render previo a la siembra el contador NO grita «+79».
  // ⛔ Es un ref y NO estado a propósito: no se toca nunca más después de la
  //    siembra, y volverlo estado forzaría un render extra sin ganar nada.
  //    El contador se repinta solo porque `contactos` SÍ es estado.
  const idsAlMontar = useRef<ReadonlySet<string> | null>(null);
  const huboCaida = useRef(false);
  const arrRef = useRef<Arrastre | null>(null);
  arrRef.current = arr;
  /** T-CAL-19 · ¿el bot mueve las tarjetas? `null` = el negocio no califica (o no se
   *  pudo leer): ni interruptor ni candado. ⚠️ Falla CERRADA hacia «no pinto»: que
   *  esta lectura falle no puede tumbar el tablero ni inventar un candado. */
  const [moverAuto, setMoverAuto] = useState<boolean | null>(null);
  const [cambiandoMover, setCambiandoMover] = useState(false);

  // ── T-LAT-3 · el chat al costado ─────────────────────────────────────────
  /** El repartidor de avisos en vivo hacia el panel lateral.
   *  ⛔ EXISTE PARA NO ABRIR UNA SEGUNDA SUSCRIPCIÓN (I-4): `channel(topic)`
   *  devuelve el canal que YA existe si el topic se repite, y un `.on()` puesto
   *  después del `subscribe` no recibe nada — sin error. El panel se quedaría
   *  sordo y nadie se enteraría. */
  const bus = useMemo(() => new EventTarget(), []);
  const [abierto, setAbierto] = useState<{ contacto: ContactoEmbudo; desde: DOMRect | null } | null>(null);
  /** Cuándo terminó el último ARRASTRE de verdad. El `click` que el navegador
   *  dispara justo después no puede abrir el panel: el asesor movió, no pulsó. */
  const ultimoSoltar = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const [es, cs] = await Promise.all([listarEtapas(sb, tenantId), listarEmbudo(sb, tenantId)]);
        setEtapas(es); setContactos(cs);
        // T-ORD-8 · I-12 · LA SIEMBRA. Montar la pantalla es el descanso natural:
        // se entra a trabajar con el ORDEN ÚTIL, y a partir de aquí ya no se
        // mueve. Un remontaje (F5, o salir de /embudo y volver) vuelve a pasar
        // por aquí, que es la única salida que dejó la decisión del § 8-2.
        for (const c of cs) columnaFijada.current.set(c.id, c.etapa_id ?? null);
        // I-40 · se siembra AQUÍ y solo aquí: a partir de este instante, una
        // tarjeta que no esté en este conjunto entró MIENTRAS él miraba.
        idsAlMontar.current = new Set(cs.map((c) => c.id));
        setFijado(ordenarTarjetas(cs).map((c) => c.id));
      } catch (e) { setError(explicarError(e)); }
      finally { setCargando(false); }
    })();
  }, [sb, tenantId]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try { const v = await leerMoverAutomatico(sb, tenantId); if (vivo) setMoverAuto(v); }
      catch { if (vivo) setMoverAuto(null); }
    })();
    return () => { vivo = false; };
  }, [sb, tenantId]);

  /** T-CAL-19 · I-129 · el dueño enciende o apaga. Se pinta lo que DEVUELVE la
   *  base, no lo pedido; si rechaza, el interruptor se queda como estaba. */
  const cambiarMover = useCallback(async (v: boolean) => {
    setCambiandoMover(true);
    try { setMoverAuto(await fijarMoverAutomatico(sb, tenantId, v)); }
    catch (e) { setError(explicarError(e)); }
    finally { setCambiandoMover(false); }
  }, [sb, tenantId]);

  /** T-CAL-19 · I-130 · «Soltar»: la tarjeta vuelve al automático; su etapa NO se
   *  toca (ni aquí ni en la base), así que no cambia de columna ni de sitio (I-12).
   *  ⭐ T-CAL-B1 · I-162 · se ofrece a TODO rol, `victorix` incluido: el anfitrión solo
   *  arma el panel con un negocio de membresía ACTIVA (`web/src/lib/auth.tsx`), así que
   *  quien ve el candado es miembro y `soltar_candado` (0074, `es_miembro`) se lo da.
   *  ⛔ No condicionarlo al rol: `rol = 'victorix'` tapa la membresía, no la niega. */
  const soltar = useCallback(async (c: ContactoEmbudo) => {
    try {
      await soltarCandado(sb, c.id);
      setContactos((p) => p.map((x) => (x.id === c.id ? { ...x, etapa_puesta_por: "bot" } : x)));
    } catch (e) { setError(explicarError(e)); }
  }, [sb]);

  // ── T-ORD-8 · I-12 · EL ÚNICO SITIO QUE MANDA UNA TARJETA AL FRENTE ────────
  // ⛔⛔ UN SOLO SITIO, Y ESA ES LA LECCIÓN DEL TRAMO. La primera versión repartía
  //    la regla en dos (`mover()` para el selector de la tarjeta, y nada para el
  //    resto) y el fiscal encontró el hueco el mismo día (H-1, 4-sep-2026): el
  //    «Mover a» de la FICHA LATERAL no pasa por `mover()` —llama a `moverEtapa`
  //    y parchea el contacto por `onContactoCambiado` (`PanelContacto.tsx:359`)—
  //    así que la tarjeta caía en la columna nueva por su índice de siembra, en
  //    mitad de la lista. Y ése es el flujo NATURAL de Cristhian: abre la
  //    tarjeta, contesta y la clasifica desde la ficha. En «Cotizado» con 20, la
  //    que acaba de clasificar se le iba fuera de la vista.
  // ⭐ Por eso esto NO se arregla en cada llamador, sino aquí: se observa el
  //    EFECTO (el contacto está en otra columna que aquella en la que se fijó) en
  //    vez de confiar en que cada camino se acuerde de avisar. Cualquier camino
  //    futuro —arrastre, inbox, una pantalla que aún no existe— queda cubierto
  //    por construcción, sin que nadie tenga que recordarlo.
  // Dos cosas van al frente, y solo dos:
  //   · lo que NUNCA se pintó (contacto nuevo);
  //   · lo que cambió de columna (lo movió una mano, y es donde el ojo la sigue).
  // ⛔ No poda los ids de contactos que ya no están: `ordenarConMemoria` los
  //    ignora, y podar obligaría a un render extra sin ganar nada.
  useEffect(() => {
    const mapa = columnaFijada.current;
    const alFrente = contactos.filter((c) => !mapa.has(c.id) || mapa.get(c.id) !== (c.etapa_id ?? null));
    if (alFrente.length === 0) return;                     // ⇠ corta el bucle
    for (const c of contactos) mapa.set(c.id, c.etapa_id ?? null);
    const ids = ordenarTarjetas(alFrente).map((c) => c.id);
    const suben = new Set(ids);
    setFijado((prev) => [...ids, ...prev.filter((x) => !suben.has(x))]);
  }, [contactos]);

  /** Vuelve a leer el embudo (contacto nuevo, o al reponer una caída del canal). */
  const recargar = useCallback(async () => {
    try { setContactos(await listarEmbudo(sb, tenantId)); }
    catch (e) { setError(explicarError(e)); }
  }, [sb, tenantId]);

  // ── T-CAL-17 · H1 del revisor (r1) · EL CALIENTE ESCALADO ENTRA AL CARRIL ──
  // 🟠 La temperatura NO viaja por Realtime (`calificaciones` no está en
  //    `suscribirInbox`): sin esto, un cliente que se calienta y pide asesor CON
  //    LA PANTALLA ABIERTA no entraba al carril hasta recargar a mano — justo el
  //    caso para el que existe la columna.
  // ⭐ La señal es la PÍLDORA del hilo (`transferido_humano` o `cliente_caliente`),
  //    no el evento `conversacion`: en los dos caminos del calificador (T-CAL-7
  //    `pasarCaliente`, T-CAL-7b `anotarCaliente`) la temperatura ya está
  //    guardada ANTES de escribir la píldora; la escalada del procesador, en
  //    cambio, puede llegar antes que la calificación (decisión del coordinador,
  //    opción A afinada).
  // ⚠️ UNA consulta por ráfaga: el turno que pasa escribe dos píldoras seguidas, y
  //    las dos caen en la MISMA recarga. El orden fijado (I-12) no se toca:
  //    `recargar()` ya existe para esto y el observador de columnas sigue igual.
  const contactosRef = useRef<ContactoEmbudo[]>([]);
  contactosRef.current = contactos;
  const recargaEnCola = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pedirRecarga = useCallback(() => {
    if (recargaEnCola.current) return;                     // ya viene una: se suma a ella
    recargaEnCola.current = setTimeout(() => { recargaEnCola.current = null; void recargar(); }, RECARGA_MS);
  }, [recargar]);
  useEffect(() => () => { if (recargaEnCola.current) clearTimeout(recargaEnCola.current); }, []);

  // ── en vivo (R-TAR-01): la MISMA suscripción del inbox, con sus 5 avisos ──
  // ⭐ T-LAT-3: cada aviso se REENVÍA por el `bus` antes de hacer lo suyo. Esa
  // es la única forma de que el panel lateral esté vivo sin abrir un segundo
  // canal (I-4). El panel filtra por conversación; aquí no se filtra nada.
  useEffect(() => {
    const reenviar = (nombre: string, detail: any) =>
      bus.dispatchEvent(new CustomEvent(nombre, { detail }));
    return suscribirInbox(sb, tenantId, {
      // T-PAN-5: en la primera suscripción no se recarga; tras una caída, sí.
      estado: (e) => {
        // 🔴 H1 · El lateral también tiene un hueco que reponer, y su hilo no lo
        // repone `recargar()` (que solo trae tarjetas). Va por el bus.
        reenviar("estado", e);
        if (e === "caido") { huboCaida.current = true; setCanalCaido(true); return; }
        setCanalCaido(false);
        if (!huboCaida.current) return;
        huboCaida.current = false;
        void recargar();
      },
      conversacion: (c: Partial<Conversacion>) => {
        reenviar("conversacion", c);
        if (!c.contacto_id) return;
        setContactos((prev) => {
          const i = prev.findIndex((x) => x.id === c.contacto_id);
          if (i === -1) { void recargar(); return prev; }        // contacto nuevo: traerlo
          const x = prev[i];
          const masReciente = !x.ultima_actividad
            || (c.ultima_actividad != null && new Date(c.ultima_actividad) >= new Date(x.ultima_actividad));
          // ⚠️ Un contacto con DOS conversaciones abiertas verá el número de la más
          //    reciente hasta la siguiente recarga (declarado en el plan).
          if (c.id !== x.conversacion_id && !masReciente) return prev;
          const copia = prev.slice();
          copia[i] = {
            ...x,
            no_leidos: c.no_leidos ?? x.no_leidos,
            ultima_actividad: c.ultima_actividad ?? x.ultima_actividad,
            conversacion_id: c.id ?? x.conversacion_id,
            estado_conversacion: c.estado ?? x.estado_conversacion,
            // T-LAT-7: sin esto, el distintivo se quedaría clavado en «Esperando
            // asesor» después de que una compañera tomara el chat — y el aviso
            // que existe para que alguien vaya diría que nadie fue.
            // ⚠️ `!== undefined` y no `??`: `null` es un VALOR aquí (volvió al
            // bot, o le quitaron el dueño), y con `??` se conservaría el valor
            // viejo para siempre.
            motivo_humano: c.motivo_humano !== undefined ? c.motivo_humano : x.motivo_humano,
            // 🔴 H-1: sin ESTA línea, «Tomar el chat» no apagaría el distintivo
            // hasta la siguiente recarga — el mismo fallo, un piso más arriba.
            asignada_a: c.asignada_a !== undefined ? c.asignada_a : x.asignada_a,
          };
          return copia;
        });
      },
      // ── T-PRE-9 · R-PRE-02 · I-13 (+ I-12) · EL PREVIO SE MUEVE SOLO ──────
      // Hasta aquí este aviso SOLO se reenviaba al panel lateral. Ahora también
      // repinta el previo de su tarjeta: llega un mensaje —del cliente, del bot o
      // nuestro, desde cualquier pantalla— y la línea cambia sin recargar.
      // ⛔⛔ SE CASA POR `conversacion_id`, NO POR CONTACTO, y es lo mismo que
      //    decidió la `0059` un piso más abajo: un contacto con DOS conversaciones
      //    abiertas tiene que enseñar el previo de la que la tarjeta ABRE. Casarlo
      //    por contacto haría que la tarjeta dijera una cosa y abriera otra.
      // ⛔⛔ Y NO SE MUEVE NADA DE SITIO (I-12, T-ORD-8). Esto toca `ultimo_texto`
      //    y `ultimo_autor` y NADA MÁS: ni `etapa_id` ni el fijado, que es lo
      //    único que el observador de arriba mira para mandar una tarjeta al
      //    frente. El previo se actualiza DONDE LA TARJETA YA ESTÁ.
      // ⚠️ Si el mensaje no es de ninguna tarjeta (una conversación cerrada, o de
      //    un contacto que este filtro no trajo) NO SE HACE NADA — y en concreto
      //    NO se dispara una recarga: sería una consulta al embudo por cada
      //    mensaje del negocio. Mismo criterio ya declarado en T-TAR-2.
      // ⚠️ Y si nada cambia se devuelve el MISMO arreglo, no una copia: un
      //    arreglo nuevo re-renderiza el tablero entero por cada mensaje.
      mensaje: (m) => {
        reenviar("mensaje", m);
        if (!m?.conversacion_id) return;
        // T-CAL-17 · H1: la píldora del caliente/escalado de un contacto del
        // tablero pide UNA recarga (trae la temperatura). Va ANTES de la salida
        // de abajo, que descarta toda fila de sistema para el previo.
        if (m.tipo === "sistema" && PILDORAS_QUE_RECARGAN.has(String(m.cuerpo?.evento ?? ""))
          && contactosRef.current.some((x) => x.conversacion_id === m.conversacion_id)) pedirRecarga();
        // ⛔⛔ UN EVENTO DEL SISTEMA NO ES PREVIO, y este es el caso REAL que lo
        //    pide (H-2 del fiscal, 5-sep): el cliente pregunta, el bot escala, y
        //    `pasar_a_humano` (`0052:103`) manda un `tipo='sistema'` por este mismo
        //    canal. La tarjeta acaba de ponerse «Esperando asesor» — la que más
        //    urge leer — y diría «· evento del sistema» en vez de la pregunta del
        //    cliente. `previoDeMensaje` devuelve `null` y aquí NO SE TOCA NADA.
        // ⚠️ Se sale ANTES del `setContactos`: escribir el `null` borraría la línea,
        //    que es otro fallo distinto y también malo.
        const texto = previoDeMensaje(m);
        if (texto === null) return;
        setContactos((prev) => {
          const i = prev.findIndex((x) => x.conversacion_id === m.conversacion_id);
          if (i === -1) return prev;
          const x = prev[i];
          if (x.ultimo_texto === texto && x.ultimo_autor === m.autor) return prev;
          const copia = prev.slice();
          copia[i] = { ...x, ultimo_texto: texto, ultimo_autor: m.autor };
          return copia;
        });
      },
      entrega: (a) => reenviar("entrega", a),
      media: (a) => reenviar("media", a),
    });
  }, [sb, tenantId, recargar, bus, pedirRecarga]);

  function cambiarPlegado(v: boolean) { setPlegado(v); guardarPlegado(tenantId, v); }

  // Mueve de etapa con actualización optimista: la tarjeta cae YA en su columna
  // y solo se devuelve si la base la rechaza.
  const mover = useCallback(async (c: Contacto, etapaId: string | null) => {
    if ((c.etapa_id ?? null) === etapaId) return;
    const antes = c.etapa_id ?? null;
    // T-ORD-8 · I-12 · SI LA BASE RECHAZA, SE DESHACE TODO — también el sitio
    // (H-3 del fiscal, 4-sep-2026). Al mover, el observador de arriba manda la
    // tarjeta al frente; si el `moverEtapa` falla (red mala, o el RLS de una
    // agente sin membresía) y solo se revirtiera `contactos`, la tarjeta volvería
    // a su columna de origen PERO EN EL PRIMER SITIO: en «Sin etapa» de 45, la
    // n.º 30 aparece 1.ª y 29 se corren bajo la mano — el daño exacto de I-12,
    // disparado por un fallo que no es suyo.
    // ⛔⛔ Y SE DEVUELVE SOLO ESTA TARJETA, NO UNA INSTANTÁNEA DEL TABLERO (H-7 y
    //    H-8 del fiscal, ronda 3). La primera versión guardaba el fijado ENTERO y
    //    lo restauraba ENTERO, así que **todo lo que pasara mientras el
    //    movimiento estaba en vuelo se perdía**: si en ese rato otra tarjeta se
    //    clasificaba desde la ficha, volvía a caer en mitad de su columna (H-1
    //    otra vez, por la puerta de atrás); y un contacto nuevo llegado en ese
    //    rato salía del fijado pero se quedaba en el mapa, con lo que era «nueva»
    //    para siempre y se colaba delante de la siguiente. Un `await` no congela
    //    el tablero: mientras se espera a la base, el mundo sigue.
    // ⭐ Se recuerda el ANCLA —el id que iba justo DELANTE— y no el índice: un
    //    número absoluto se desplaza en cuanto algo entra por el frente (una
    //    recarga, un contacto nuevo), y devolvería la tarjeta un puesto corrido.
    const iAntes = fijadoRef.current.indexOf(c.id);
    const estabaFijada = iAntes >= 0;
    const ancla = iAntes > 0 ? fijadoRef.current[iAntes - 1] : null;
    // T-CAL-19 · I-130 · mover a mano pone el candado en la base (`mover_etapa` con
    // JWT ⇒ `equipo`, 0069): la tarjeta lo enseña ya, y si la base rechaza, vuelve.
    const antesPor = (c as ContactoEmbudo).etapa_puesta_por ?? null;
    setContactos((p) => p.map((x) => (x.id === c.id ? { ...x, etapa_id: etapaId, etapa_puesta_por: "equipo" } : x)));
    try {
      await moverEtapa(sb, c.id, etapaId);
    } catch (e) {
      setContactos((p) => p.map((x) => (x.id === c.id ? { ...x, etapa_id: antes, etapa_puesta_por: antesPor } : x)));
      // ⚠️ El mapa PRIMERO: si no, el observador ve la vuelta como un cambio de
      //    columna más y la manda al frente otra vez — el mismo daño por detrás.
      //    Y se toca SOLO la entrada de esta tarjeta, por lo mismo que el fijado.
      columnaFijada.current.set(c.id, antes);
      setFijado((p) => {
        if (!estabaFijada) return p;              // nunca tuvo sitio: no se le inventa uno
        const sin = p.filter((x) => x !== c.id);
        if (ancla === null) return [c.id, ...sin];  // era la primera: vuelve a serlo
        const j = sin.indexOf(ancla);
        // El ancla se fue (a esa otra tarjeta la borraron mientras tanto): se deja
        // como está en vez de inventarle un sitio. Declarado, no tapado.
        return j === -1 ? p : [...sin.slice(0, j + 1), c.id, ...sin.slice(j + 1)];
      });
      setError(explicarError(e));
    }
  }, [sb]);

  /** Clic (o Enter/Espacio) en la tarjeta ⇒ el chat al costado (R-LAT-01).
   *  ⛔ LAS DOS GUARDAS CONTRA EL ARRASTRE:
   *   1. mientras se arrastra, no se abre nada;
   *   2. y durante 250 ms DESPUÉS de soltar tampoco — el navegador dispara un
   *      `click` al terminar el gesto, y sin esto cada arrastre terminaría con
   *      el chat abierto encima del tablero. */
  const abrir = useCallback((c: ContactoEmbudo, el: HTMLElement) => {
    if (arrRef.current?.activo) return;
    if (Date.now() - ultimoSoltar.current < 250) return;
    // `getBoundingClientRect` puede no existir en un doble de prueba: sin
    // rectángulo el panel abre sin animación, que es un camino legítimo (I-10).
    const desde = typeof el?.getBoundingClientRect === "function" ? el.getBoundingClientRect() : null;
    setAbierto({ contacto: c, desde });
  }, []);

  // ── arrastre 1:1 ────────────────────────────────────────────────────────
  // ⭐ FLUIDEZ (2-nov-2026): estable (`useCallback` sin dependencias: solo usa
  // `setArr`) para que `TarjetaMemo` no se repinte entera en cada pixel del
  // arrastre. Antes CADA `pointermove` repintaba todas las tarjetas del tablero.
  const empezar = useCallback((e: React.PointerEvent, c: Contacto) => {
    const tarjeta = (e.currentTarget as HTMLElement).closest("[data-contacto]") as HTMLElement | null;
    if (!tarjeta) return;
    const r = tarjeta.getBoundingClientRect();
    // La captura mantiene el seguimiento aunque el puntero se salga del asa.
    // Puede fallar (puntero ya liberado, eventos sintéticos): no es fatal,
    // el seguimiento real lo hacen los listeners de window.
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* sin captura */ }
    setArr({
      contactoId: c.id, desdeEtapa: c.etapa_id ?? null,
      x: e.clientX, y: e.clientY,
      dx: e.clientX - r.left, dy: e.clientY - r.top,
      ancho: r.width, alto: r.height, activo: false,
    });
  }, []);

  useEffect(() => {
    if (!arr) return;
    const mover_ = (e: PointerEvent) => {
      const a = arrRef.current;
      if (!a) return;
      const activo = a.activo || Math.hypot(e.clientX - a.x, e.clientY - a.y) > UMBRAL;
      setArr({ ...a, x: e.clientX, y: e.clientY, activo });
      if (!activo) return;
      // columna bajo el puntero (el fantasma no intercepta: pointer-events none)
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const col = el?.closest("[data-etapa]") as HTMLElement | null;
      setDestino(col ? (col.dataset.etapa || null) : undefined);
    };
    const soltar = () => {
      const a = arrRef.current;
      // Solo un arrastre DE VERDAD bloquea el clic siguiente: un toque en el asa
      // que no llegó al umbral no tiene por qué dejar la tarjeta muerta 250 ms.
      if (a?.activo) ultimoSoltar.current = Date.now();
      setArr(null);
      const d = destino;
      setDestino(undefined);
      if (!a?.activo || d === undefined) return;
      const c = contactos.find((x) => x.id === a.contactoId);
      if (c) {
        void mover(c, d);
        // Si cayó en una etapa final, «Cerradas» se queda desplegada: plegarse
        // sola debajo de la mano escondería la tarjeta que se acaba de soltar.
        if (d && etapas.find((e) => e.id === d)?.es_final) cambiarPlegado(false);
      }
    };
    window.addEventListener("pointermove", mover_);
    window.addEventListener("pointerup", soltar);
    window.addEventListener("pointercancel", soltar);
    return () => {
      window.removeEventListener("pointermove", mover_);
      window.removeEventListener("pointerup", soltar);
      window.removeEventListener("pointercancel", soltar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arr, destino, contactos, mover, etapas]);

  const enArrastre = contactos.find((c) => c.id === arr?.contactoId);
  const conteo: Record<string, number> = {};
  for (const c of contactos) if (c.etapa_id) conteo[c.etapa_id] = (conteo[c.etapa_id] ?? 0) + 1;

  const vivas = etapas.filter((e) => !e.es_final);
  const finales = etapas.filter((e) => e.es_final);
  // T-ORD-8 · I-12 · el orden de la columna sale de la MEMORIA, no del recálculo.
  const deEtapa = (id: string | null) =>
    ordenarConMemoria(contactos.filter((c) => (c.etapa_id ?? null) === id), fijado);
  const columnas: { etapa: Etapa | null; items: ContactoEmbudo[] }[] = [
    ...vivas.map((e) => ({ etapa: e, items: deEtapa(e.id) })),
  ];
  const sinEtapa = deEtapa(null);
  if (sinEtapa.length) columnas.unshift({ etapa: null, items: sinEtapa });
  const columnasFinales = finales.map((e) => ({ etapa: e, items: deEtapa(e.id) }));
  const enCerradas = columnasFinales.reduce((s, c) => s + c.items.length, 0);
  // Durante un arrastre activo la franja se despliega sola: hay que poder soltar
  // en una final concreta («Cerradas» a secas no es una etapa).
  const cerradasDesplegadas = finales.length > 0 && (!plegado || !!arr?.activo);

  const editor = editorAbierto && (
    <EtapasEditor sb={sb} tenantId={tenantId} etapas={etapas} conteo={conteo}
      onCambio={setEtapas} onCerrar={() => setEditorAbierto(false)} />
  );

  if (!cargando && etapas.length === 0) {
    return <>
      <Vacio icono={<Users size={40} strokeWidth={1.25} />} titulo="El embudo no tiene etapas"
        accion={puedeEditarEtapas
          ? <Boton tono="marca" onClick={() => setEditorAbierto(true)}><SlidersHorizontal size={14} /> Crear etapas</Boton>
          : undefined}>
        {puedeEditarEtapas
          ? "Crea las etapas del negocio para empezar a mover clientes."
          : "Pídele al dueño del negocio que cree las etapas para empezar a mover clientes."}
      </Vacio>
      {editor}
    </>;
  }

  // ⚠️ Función que PINTA, no componente interno: un componente definido dentro del
  // render cambia de identidad en cada render y React lo desmonta y remonta
  // (se pierde el scroll de la columna y parpadea).
  function pintarColumna(etapa: Etapa | null, items: ContactoEmbudo[]) {
    const id = etapa?.id ?? null;
    const activoDestino = arr?.activo && destino === id;
    const nuevasAqui = cuantasNuevas(items, idsAlMontar.current);
    return (
      <section
        key={id ?? "sin"}
        data-etapa={id ?? ""}
        data-final={etapa?.es_final ? "1" : undefined}
        className={`w-[260px] sm:w-[280px] shrink-0 flex flex-col rounded-xl border transition-colors
          ${activoDestino ? "border-marca bg-marca-suave/50" : "border-borde-suave bg-elevado/60"}`}
      >
        <header className="shrink-0 flex items-center gap-2 px-3 py-2.5 border-b border-borde-suave">
          <span className="h-2 w-2 rounded-full shrink-0"
            style={{ background: etapa?.color ?? "var(--color-tinta-2)" }} aria-hidden="true" />
          <h2 className="text-[13.5px] font-semibold truncate">{etapa?.nombre ?? "Sin etapa"}</h2>
          {/* I-40 · cuántas ENTRARON desde que abrió la pantalla. Se apaga solo
              al recargar, que es cuando vuelve a empezar a mirar. */}
          {nuevasAqui > 0 && (
            <span data-nuevas={nuevasAqui} title={`${nuevasAqui} desde que abriste la pantalla`}
              className="ml-auto shrink-0 text-[11.5px] font-semibold text-marca cifra">
              +{nuevasAqui}
            </span>
          )}
          <span className={`${nuevasAqui > 0 ? "" : "ml-auto"} text-[12px] text-tinta-2 cifra`}>{items.length}</span>
        </header>
        <div className="flex-1 overflow-y-auto scroll-fino p-2 space-y-2 min-h-[120px]">
          {items.length === 0 && (
            <p className="text-[12.5px] text-tinta-2 text-center py-6 px-2">
              {activoDestino ? "Suelta aquí" : "Vacía"}
            </p>
          )}
          {items.map((c) => (
            <TarjetaMemo key={c.id} c={c} etapas={etapas} arrastrando={arr?.contactoId === c.id && arr.activo}
              onEmpezar={empezar} onMover={mover} onAbrir={abrir}
              candado={moverAuto !== null && c.etapa_puesta_por === "equipo"} onSoltar={soltar} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="h-full flex flex-col min-w-0">
      {/* T-PAN-5 · la misma franja del inbox: avisa cuando el canal SE DECLARA caído. */}
      {canalCaido && (
        <div role="status" aria-live="polite" data-franja="canal"
          className="shrink-0 flex items-center gap-2 px-3 md:px-4 py-2 text-[13px] leading-snug
            border-b border-borde bg-tenue text-tinta-2">
          <WifiOff size={15} strokeWidth={1.75} className="shrink-0" aria-hidden="true" />
          <span>Sin conexión con el servidor. Reintentando; lo que llegue mientras tanto se pondrá al día solo.</span>
        </div>
      )}
      <div className="shrink-0 flex items-start gap-3 px-4 sm:px-6 pt-4 pb-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[19px] font-semibold tracking-tight">Embudo</h1>
          <p className="text-[13px] text-tinta-2 mt-0.5">
            Arrastra por el asa, o usa <span className="font-medium">Mover a</span> en cada tarjeta.
          </p>
          {moverAuto !== null && (
            <InterruptorMover valor={moverAuto} puede={puedeCambiarMover} ocupado={cambiandoMover}
              onCambiar={(v) => void cambiarMover(v)} />
          )}
        </div>
        {puedeEditarEtapas && (
          <Boton tono="neutro" tam="sm" onClick={() => setEditorAbierto(true)} aria-label="Etapas del embudo"
            className="shrink-0 mt-0.5">
            <SlidersHorizontal size={14} /> Etapas
          </Boton>
        )}
      </div>

      {error && (
        <div className="mx-4 sm:mx-6 mb-2">
          <div role="alert" className="text-[13px] bg-peligro/10 text-peligro border border-peligro/25 rounded-lg px-3 py-2">
            {error} <button className="underline cursor-pointer ml-2" onClick={() => setError(null)}>cerrar</button>
          </div>
        </div>
      )}

      {/* T-CAL-17 · el carril va FUERA del tablero que se desplaza: arriba en
          celular, columna fija a la izquierda desde `sm`. Se deriva de `contactos`
          en cada render —no es una copia guardada—, así que tomar el chat por
          Realtime (`asignada_a`) lo saca solo, sin recargar (I-122). */}
      <div className="flex-1 min-h-0 min-w-0 flex flex-col sm:flex-row">
      {!cargando && <CarrilAtenderYa items={atenderYa(contactos)} onAbrir={abrir} />}
      <div className="flex-1 min-h-0 min-w-0 overflow-x-auto scroll-fino px-4 sm:px-6 pb-5">
        <div className="flex gap-3 h-full min-w-max">
          {cargando && Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="w-[260px] shrink-0 space-y-2">
              <div className="skeleton h-6 w-28 rounded" />
              <div className="skeleton h-20 rounded-xl" /><div className="skeleton h-20 rounded-xl" />
            </div>
          ))}

          {!cargando && columnas.map(({ etapa, items }) => pintarColumna(etapa, items))}

          {/* ── «Cerradas»: las etapas finales, plegadas en una franja (R-EMB-02) ── */}
          {!cargando && finales.length > 0 && !cerradasDesplegadas && (
            <button
              data-franja="cerradas" aria-expanded={false}
              onClick={() => cambiarPlegado(false)}
              title="Ver las etapas cerradas"
              className="w-14 shrink-0 flex flex-col items-center justify-start gap-2 pt-3 rounded-xl border
                border-dashed border-borde bg-elevado/40 text-tinta-2 hover:text-tinta hover:border-tinta/30
                transition-colors cursor-pointer"
            >
              <ChevronsLeft size={16} aria-hidden="true" />
              <span className="text-[12px] font-semibold [writing-mode:vertical-rl] rotate-180 tracking-wide">Cerradas</span>
              <span className="text-[11px] cifra [writing-mode:vertical-rl] rotate-180">
                {finales.length} {finales.length === 1 ? "columna" : "columnas"} · {enCerradas} {enCerradas === 1 ? "contacto" : "contactos"}
              </span>
            </button>
          )}
          {!cargando && cerradasDesplegadas && (
            <div data-franja="cerradas" aria-expanded={true}
              className="flex gap-3 h-full pl-3 border-l border-dashed border-borde">
              {columnasFinales.map(({ etapa, items }) => pintarColumna(etapa, items))}
              <button onClick={() => cambiarPlegado(true)} title="Plegar las etapas cerradas"
                aria-label="Plegar las etapas cerradas"
                className="w-8 shrink-0 flex flex-col items-center pt-3 rounded-xl text-tinta-2 hover:text-tinta
                  hover:bg-elevado/60 transition-colors cursor-pointer">
                <ChevronsRight size={16} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Fantasma: la tarjeta pegada al puntero (no intercepta eventos) */}
      {arr?.activo && enArrastre && (
        <div
          className="fixed pointer-events-none z-50 arrastrando bg-superficie border border-marca/40 rounded-xl p-2.5"
          style={{ left: arr.x - arr.dx, top: arr.y - arr.dy, width: arr.ancho }}
        >
          <div className="flex items-center gap-2">
            <Avatar nombre={nombreMostrado(enArrastre)} size={32} />
            <p className="text-[13.5px] font-medium truncate">{nombreMostrado(enArrastre)}</p>
          </div>
        </div>
      )}

      {/* ── T-LAT-3 · el chat al costado ─────────────────────────────────────
          Se le pasa la tarjeta VIVA (la de `contactos`, no la copia del clic):
          así lo que se edita en la ficha se ve en la tarjeta y en el panel a la
          vez, sin recargar. */}
      {abierto && (
        <PanelContacto
          sb={sb} tenantId={tenantId} usuarioId={ctx.usuarioId} rol={rol}
          contacto={contactos.find((x) => x.id === abierto.contacto.id) ?? abierto.contacto}
          etapas={etapas}
          desde={abierto.desde}
          bus={bus}
          canalCaidoAlAbrir={canalCaido}
          onCerrar={() => setAbierto(null)}
          onContactoCambiado={(parche) => setContactos((p) =>
            p.map((x) => (x.id === abierto.contacto.id ? { ...x, ...parche } : x)))}
        />
      )}

      {editor}
    </div>
  );
}
